import React, { useState } from 'react';
import { Box, Heading, HStack, VStack, Button, Input, Textarea, Table, Thead, Tbody, Tr, Th, Td, Card, CardBody, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel, IconButton, Text, Select } from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const SettingsSections = () => {
  const { t } = useLanguage();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [q, setQ] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', name_en: '', name_de: '', description: '' });

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const { data: sections } = useQuery(['sections_manage', q, page, pageSize], () => api.get(`/api/meta/sections?q=${encodeURIComponent(q)}&limit=${pageSize}&offset=${page*pageSize}`).then(res => res.data.data.sections));

  const createSection = useMutation((data) => api.post('/api/meta/sections', data), { onSuccess: () => { queryClient.invalidateQueries(['sections_manage', q]); setIsOpen(false); setForm({ code: '', name_en: '', name_de: '', description: '' }); toast({ title: t('common.success'), status: 'success' }); }});
  const updateSection = useMutation(({ id, data }) => api.put(`/api/meta/sections/${id}`, data), { onSuccess: () => { queryClient.invalidateQueries(['sections_manage', q]); setIsOpen(false); setEditing(null); toast({ title: t('common.success'), status: 'success' }); }});
  const deleteSection = useMutation((id) => api.delete(`/api/meta/sections/${id}`), { onSuccess: () => { queryClient.invalidateQueries(['sections_manage', q]); toast({ title: t('common.success'), status: 'success' }); }});

  const openCreate = () => { setEditing(null); setForm({ code: '', name_en: '', name_de: '', description: '' }); setIsOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ code: s.code || '', name_en: s.name_en || '', name_de: s.name_de || '', description: s.description || '' }); setIsOpen(true); };

  const saveSection = () => {
    if (!form.name_en && !form.name_de) { toast({ title: t('common.error'), description: 'Name required', status: 'error' }); return; }
    if (editing) updateSection.mutate({ id: editing.id, data: form }); else createSection.mutate(form);
  };

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">{t('settings.sections') || 'Sections'}</Heading>
        <HStack>
          <Input placeholder={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} maxW="250px" />
          <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={openCreate}>{t('common.new') || 'New'}</Button>
        </HStack>
      </HStack>

      <Card>
        <CardBody>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>{t('sections.code') || 'Code'}</Th>
                <Th>{t('sections.name_en') || 'Name (EN)'}</Th>
                <Th>{t('sections.name_de') || 'Name (DE)'}</Th>
                <Th>{t('sections.description') || 'Description'}</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {sections?.map(s => (
                <Tr key={s.id}>
                  <Td>{s.id}</Td>
                  <Td>{s.code}</Td>
                  <Td>{s.name_en}</Td>
                  <Td>{s.name_de}</Td>
                  <Td>{s.description || '-'}</Td>
                  <Td>
                    <HStack>
                      <Button size="xs" variant="outline" onClick={() => openEdit(s)}>{t('common.edit')}</Button>
                      <IconButton aria-label="delete" size="xs" colorScheme="red" icon={<FiTrash2 />} onClick={() => deleteSection.mutate(s.id)} />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <HStack justify="space-between" mt={4}>
            <HStack>
              <Button size="sm" onClick={() => setPage(p => Math.max(0, p-1))} isDisabled={page===0}>Prev</Button>
              <Button size="sm" onClick={() => setPage(p => p+1)} isDisabled={(sections?.length||0) < pageSize}>Next</Button>
            </HStack>
            <HStack>
              <Text fontSize="sm">Rows:</Text>
              <Select size="sm" value={pageSize} onChange={(e)=>{setPage(0); setPageSize(Number(e.target.value));}} maxW="80px">
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Select>
            </HStack>
          </HStack>
        </CardBody>
      </Card>

      {/* Create/Edit Section */}
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? t('common.edit') : t('common.new')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3}>
              <HStack w="100%">
                <FormControl maxW="200px">
                  <FormLabel>{t('sections.code') || 'Code'}</FormLabel>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('sections.name_en') || 'Name (EN)'}</FormLabel>
                  <Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('sections.name_de') || 'Name (DE)'}</FormLabel>
                  <Input value={form.name_de} onChange={(e) => setForm({ ...form, name_de: e.target.value })} />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>{t('sections.description') || 'Description'}</FormLabel>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} variant="ghost" onClick={() => setIsOpen(false)}>{t('common.cancel')}</Button>
            <Button colorScheme="brand" onClick={saveSection} isLoading={createSection.isLoading || updateSection.isLoading}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SettingsSections;
